import React, { SyntheticEvent, useEffect, useState } from 'react';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { Button, Snackbar } from '@mui/material';
import { Employee } from '../types/Employee';
import { errorPopulator } from '../utility/inputUtils';
import { useGetEmployeeById } from '../services/react-query/employees/useGetEmployeeById';
import { useUpdateEmployee } from '../services/react-query/employees/useUpdateEmployee';
import { JsonifyFile } from '../utility/functions';
import PasswordValidator from '../components/PasswordValidator';
import CustomAxios from '../utility/customAxios';
import { inputDividerStyling } from '../styles/sxStyling';

type PasswordData = {
  currentPassword: string,
  newPassword: string,
  confirmedPassword: string,
};

const ProfileBox: React.FC = () => {
  const [employee, setEmployee] = useState<Employee | null>(null);

  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const handleAvatarChange = (event: SyntheticEvent) => {
    const targetComponent: any = event.nativeEvent.target;
    setAvatarFile(targetComponent.files[0] || null);
  };

  const { data: currentEmployeeData } = useGetEmployeeById({ uuid: 'current' });
  useEffect(() => {
    if (currentEmployeeData) {
      setEmployee(currentEmployeeData);
    }
  }, [currentEmployeeData]);

  const { mutate: updateEmployee } = useUpdateEmployee({
    uuid: 'current',
    onSuccessCallback: () => console.log('updated'),
  });

  if (employee === null) return null;

  const handleSave = async () => {
    const data = { ...employee };
    if (avatarFile) data['avatar'] = await JsonifyFile(avatarFile);
    updateEmployee(data);
  };

  const updateEmployeeData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEmployee = {
      ...employee,
      [event.target.id]: event.target.value
    };
    setEmployee(newEmployee as Employee);
  };

  return <Box component={Paper} sx={{ ...inputDividerStyling, px: 1 }}>
    <TextField
      type="file"
      slotProps={{
        input: { inputProps: { accept: "image/*" } }
      }}
      onChange={handleAvatarChange}
      sx={{ width: 1 }}
    />
    <TextField
      id="chosen_name"
      label="Chosen Name"
      onChange={updateEmployeeData}
      value={employee?.chosen_name ?? ""}
      {...errorPopulator(employee, 'chosen_name')}
    />
    <TextField
      id="first_name"
      label="First Name"
      onChange={updateEmployeeData}
      value={employee?.first_name ?? ""}
      {...errorPopulator(employee, 'first_name')}
    />
    <TextField
      id="last_name"
      label="First Name"
      onChange={updateEmployeeData}
      value={employee?.last_name ?? ""}
      {...errorPopulator(employee, 'last_name')}
    />
    <TextField
      id="email"
      label="Email"
      onChange={updateEmployeeData}
      value={employee?.email ?? ""}
      {...errorPopulator(employee, 'email')}
    />
    <TextField
      id="phone"
      label="Phone"
      onChange={updateEmployeeData}
      value={employee?.phone ?? ""}
      {...errorPopulator(employee, 'phone')}
    />
    <Button variant="contained" sx={{ my: 2 }} onClick={handleSave}>
      Update
    </Button>
  </Box>;
};

const UserSettings: React.FC = () => {
  const [passwordData, setPasswordData] = useState<PasswordData>({
    currentPassword: '',
    newPassword: '',
    confirmedPassword: '',
  });
  const [updated, setUpdated] = useState<boolean>(false);
  const [resetDisabled, setResetDisabled] = useState<boolean>(true);

  const updatePasswordData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPasswordData = {
      ...passwordData,
      [event.target.id]: event.target.value
    };
    setPasswordData(newPasswordData as PasswordData);
  };

  const onSend = async () => {
    try {
      await CustomAxios.post('password/internal-reset', {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
        confirmed_password: passwordData.confirmedPassword,
      });
    } catch (e: any) {
      const errMsg = e?.response?.data?.error;
      alert(errMsg || 'There was an unexpected error resetting your password. Please try again or contact support.');
    }
    setUpdated(true);
  };

  return <Container maxWidth="xs">
    <ProfileBox />
    <Box component={Paper} sx={{ ...inputDividerStyling, my: 2, px: 1 }}>
      <TextField
        autoFocus
        required
        id="currentPassword"
        label="Current Password"
        type="password"
        onChange={updatePasswordData}
        value={passwordData.currentPassword}
      />
      <TextField
        required
        id="newPassword"
        label="New Password"
        type="password"
        onChange={updatePasswordData}
        value={passwordData.newPassword}
      />
      <TextField
        required
        id="confirmedPassword"
        label="Verify New Password"
        type="password"
        onChange={updatePasswordData}
        value={passwordData.confirmedPassword}
      />
      <PasswordValidator password={passwordData.newPassword} validatePassword={passwordData.confirmedPassword} validCallback={(passwordValid) => setResetDisabled(!passwordValid)} />
      <Button fullWidth variant="contained" sx={{ my: 2 }} onClick={onSend} disabled={resetDisabled}>
        Reset Password
      </Button>
    </Box>
    <Snackbar
      open={updated}
      onClose={() => setUpdated(false)}
      message="Updated!"
    />
  </Container>;
};

export default UserSettings;